import { get, omitBy } from "lodash/fp";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Checkbox,
  Container,
  Content,
  FlexboxGrid,
  Footer,
  Header,
  Pagination,
  Table,
  Input,
  InputGroup,
} from "rsuite";
import SearchIcon from "@rsuite/icons/Search";
import CloseIcon from "@rsuite/icons/Close";
import { ChartServices } from "../../../core/services/ChartServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { SongFields, SongListParams } from "../Songs/SongDataTypes";
import { Permission } from "../../../core/types/PermissionInterface";
import "./Charts.css";
import {
  isEmptyVal,
  isNotEmpty,
  displayField,
  genreTitle,
} from "../../../utils/helpers";
import CustomCell from "../../components/Elements/CustomCell";
import CustomHeaderCell from "../../components/Elements/CustomHeaderCell";
import CheckCell from "../../components/Elements/CheckCell";
import { ContentRoutes } from "../../../navigation/NavConstant";
import { ToastContainer } from "react-toastify";
import {
  messageTypeArr,
  appConstObj,
  roleTypeArr,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import SingerCell from "../../components/Elements/SingerCell";
import { isEmpty } from "lodash";
import SongInfoCell from "../../components/Elements/SongInfoCell";
import MediaPlayerFormatter from "../../components/Elements/MediaPlayerFormatter";
import ChartSongBulkAction from "./ChartSongBulkAction";
import { useMediaContext } from "../../../context/MediaContext";
import AudioPlayer from "../../components/Elements/AudioPlayer";

function ChartSongs({
  chartId,
  chartData,
  withHeader = true,
  showPlayer = true,
  onDeleteSongs,
  enableBulkAction = [],
  ...props
}: {
  chartId: number;
  chartData?: any;
  withHeader?: boolean;
  showPlayer?: boolean;
  onDeleteSongs?: Function;
  enableBulkAction?: string[];
}) {
  const { t } = useTranslation();
  const { Cell, Column } = Table;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(chartData?.total_songs);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<SongFields[]>([]);
  const [sort_by, setSortBy] = useState<any>();
  const [sort_order, setSortOrder] = useState<any>();
  const [smartSearchInput, setSmartSearch] = useState<string>("");
  const loggedInUserDetail = UtilityServices.getLoggedInUserDetails();
  const initSearchFormData = {
    smart_search: "",
  };
  const { playingMediaInfo, setPlayingMediaInfo, isPlaying, setIsPlaying } =
    useMediaContext();
  const [formData, setFormData] = useState<SongListParams>(initSearchFormData);
  const navigate = useNavigate();
  const [checkedKeys, setCheckedKeys] = useState<any[]>([]);
  let checked = false;
  let indeterminate = false;

  if (checkedKeys?.length === data?.length) {
    checked = true;
  } else if (checkedKeys?.length === 0) {
    checked = false;
  } else if (checkedKeys?.length > 0 && checkedKeys?.length < data?.length) {
    indeterminate = true;
  }

  const handleCheckAll = (value: any, checked: any) => {
    const keys = checked ? data.map((item) => item.id) : [];
    setCheckedKeys(keys);
  };

  const handleCheck = (value: number, checked: any) => {
    const keys = checked
      ? [...checkedKeys, value]
      : checkedKeys.filter((item) => item !== value);
    setCheckedKeys(keys);
  };

  async function getChartSongs(params: SongListParams) {
    try {
      if (isEmptyVal(chartId)) return;
      setLoading(true);
      const res = await ChartServices.getChartSongs(chartId, params);
      setData(get("data.data", res));
      setTotal(get("data.total", res));
      setCheckedKeys([]);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  function init() {
    setRows(chartData?.total_songs);
    getChartSongs(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: chartData?.total_songs,
      })
    );
  }

  useEffect(() => {
    init();
  }, [chartId, chartData]);

  async function bulkDeleteAction() {
    try {
      let params = { song_ids: checkedKeys.join() };
      let res = await ChartServices.deleteSongsFromChart(chartId, params);
      UtilityServices.showToaster({
        message: res.data.message,
      });
      onDeleteSongs && onDeleteSongs(chartId);
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  function getSearchParams(data?: any) {
    let paramsData = {
      ...formData,
      ...{ smart_search: smartSearchInput },
      ...{ sort_by, sort_order },
      ...{ rows },
      ...data,
    };
    let params: SongListParams = omitBy(isEmptyVal, paramsData);
    if (data?.page) {
      params.page = data?.page;
    }
    return params;
  }

  function onSortChange(dataKey: any, sortType: any) {
    setPage(1);
    setSortBy(dataKey);
    setSortOrder(sortType);
    getChartSongs(
      getSearchParams({
        page: 1,
        sort_by: dataKey,
        sort_order: sortType,
      })
    );
  }

  const filterChartSongs = (e: any) => {
    if (e.key === "Enter" || e.type === "click") {
      getChartSongs(getSearchParams({ smart_search: smartSearchInput }));
    }
  };

  const clearSmartSearch = () => {
    setSmartSearch("");
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    getChartSongs(
      getSearchParams({
        smart_search: "",
        page: 1,
        sort_by: null,
        sort_order: null,
      })
    );
  };

  const deletePermission = UtilityServices.hasPermission("delete-song");
  const permissionObj: Permission = {
    delete: deletePermission,
  };

  return (
    <div className="chart-songs">
      <Container className="container">
        {withHeader && (
          <Header className="h4">
            <FlexboxGrid justify="space-between">
              <FlexboxGrid.Item colspan={24}>
                <h4>
                  {t("CHART")} {chartData?.chart_title} {t("SONGS")}
                </h4>
              </FlexboxGrid.Item>
            </FlexboxGrid>
          </Header>
        )}

        <Content className="chart-songs">
          {UtilityServices.hasLoggedInUserType(roleTypeArr.backend) && (
            <>
              <div className="row chart-song-controls-row">
                <ChartSongBulkAction
                  chartData={chartData}
                  checkedKeys={checkedKeys}
                  setCheckedKeys={setCheckedKeys}
                  onDeleteSongs={bulkDeleteAction}
                  {...(enableBulkAction.length > 0 && { enableBulkAction })}
                />
                <div className="col-md-5">
                  <div className="chart-song-smart-search-wrap">
                    <InputGroup className="chart-song-smart-search">
                    <Input
                      className="form-control"
                      placeholder={t("SMART_SEARCH_BY", {
                        entity:
                          t("SONG").toLowerCase() +
                          " " +
                          t("NAME").toLowerCase() +
                          ", " +
                          t("SINGER").toLowerCase() +
                          ", " +
                          t("OR").toLowerCase() +
                          " " +
                          t("NAME").toLowerCase(),
                      })}
                      value={smartSearchInput}
                      onChange={(val: any) => setSmartSearch(val)}
                      onKeyDown={(e: any) => filterChartSongs(e)}
                    />
                    <InputGroup.Button
                      title={t("SEARCH")}
                      onClick={(e: any) => filterChartSongs(e)}
                    >
                      <SearchIcon />
                    </InputGroup.Button>
                    <InputGroup.Button
                      title={t("CLEAR")}
                      onClick={() => clearSmartSearch()}
                    >
                      <CloseIcon />
                    </InputGroup.Button>
                    </InputGroup>
                  </div>
                </div>
              </div>
            </>
          )}

          <div className={total > 0 ? "song-table" : "no-record-table"}>
            <Table
              height={400}
              fillHeight={true}
              data={data}
              loading={loading}
              onSortColumn={onSortChange}
              sortColumn={sort_by}
              bordered={true}
              cellBordered
              sortType={sort_order}
              hover={true}
              renderEmpty={() => (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  <h4>
                    {t("NO_ENTITY_FOUND_FOR", {
                      entity: t("SONG"),
                      for: chartData?.chart_title,
                    })}
                  </h4>
                </div>
              )}
            >
              <Column width={40} align="center" fixed>
                <CustomHeaderCell>
                  <div>
                    <Checkbox
                      inline
                      checked={checked}
                      indeterminate={indeterminate}
                      onChange={handleCheckAll}
                    />
                  </div>
                </CustomHeaderCell>
                <CheckCell
                  dataKey="id"
                  checkedKeys={checkedKeys}
                  onChange={handleCheck}
                />
              </Column>
              <Column width={60} align="center" fixed>
                <CustomHeaderCell>{t("PLAY")}</CustomHeaderCell>
                <MediaPlayerFormatter
                  rowData={(rowData: any) => rowData}
                  mediaUrlKey={"media_url"}
                  mediaImageKey={"media_image"}
                />
              </Column>
              <Column width={200} sortable resizable flexGrow={1}>
                <CustomHeaderCell>{t("NAME")}</CustomHeaderCell>
                <CustomCell dataKey="title">
                  {(rowData: any) => (
                    <span title={`${rowData.title}`}>
                      {displayField(rowData.title)}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column width={160}>
                <CustomHeaderCell>{t("SINGER")}</CustomHeaderCell>
                <SingerCell rowData={(rowData: any) => rowData} />
              </Column>
              <Column width={100} sortable resizable>
                <CustomHeaderCell>{t("GENRE")}</CustomHeaderCell>
                <CustomCell dataKey="genre_title">
                  {(rowData: any) => (
                    <span title={genreTitle(rowData)}>
                      {displayField(rowData.genre_title)}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column width={80}>
                <CustomHeaderCell>{t("MORE")}</CustomHeaderCell>
                <SongInfoCell
                  rowData={(rowData: any) => rowData}
                  showData={[
                    "era",
                    "mood",
                    "language",
                    "lyricist",
                    "composer",
                    "tempo",
                  ]}
                />
              </Column>
            </Table>
          </div>
        </Content>
        <Footer className="footer">
          <Pagination
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={["total"]}
            total={total}
          />
        </Footer>
        {showPlayer && <AudioPlayer />}
        <ToastContainer />
      </Container>
    </div>
  );
}

export default ChartSongs;
